<template>
    <div>
        <template v-if="! ready">
            <sau-loading>
                <template slot="message">Fetching Account Details</template>
            </sau-loading>
        </template>
        <template v-else>
            <div class="flex items-center mb-12">
                <div class="border rounded-full mr-4">
                    <img
                        v-b-tooltip
                        :src="profileWidget.avatar"
                        class="w-20 h-20 rounded-full"
                        title="Image provided by Gravatar based on the email."
                    />
                </div>
                <div class="flex-1">
                    <h5 class="sau-para-m">{{profileWidget.firstname}} {{profileWidget.lastname}}</h5>
                    {{ isEditingAccountHolderProfile ? accountHolder.email : userDetails.email }}
                    <button
                        type="button"
                        v-b-modal.update-email
                        v-if="! isEditingAccountHolderProfile"
                        class="sau-linx green sau-font sm pl-2"
                    >
                        <i class="fas fa-pencil-alt"></i>
                    </button>
                    <sau-update-email />
                    <span
                        :title="popup"
                        v-b-tooltip.hover
                        v-if="isEditingAccountHolderProfile"
                        class="sau-help sm ml-2 align-middle"
                    >?</span>
                </div>
            </div>

            <form ref="form">
                <div class="-m-4 flex flex-col md:flex-row">
                    <div class="m-4 w-full md:w-1/4">
                        <h4>Registration Details</h4>
                        <template
                            v-if="[
                                startedWithLastNameProvided,
                                startedWithFirstNameProvided,
                                startedWithCompanyNameProvided,
                            ].includes(true) && (isEditingAccountHolderProfile || isAccountHolder)"
                        >
                            <p class="text-sm">
                                Contact us to update your name, company name, or ABN.
                            </p>
                            <p class="text-sm">
                                <a href="/support" class="text-green">
                                    <i class="fas fa-comment"></i>
                                    Contact
                                </a>
                            </p>
                        </template>
                        <template v-else>
                            <p class="text-sm">
                                Contact details for your account.
                            </p>
                        </template>
                    </div>

                    <div class="m-4 w-full md:w-3/4">
                        <div class="border p-8">
                            <div class="row">
                                <div class="col-lg-6">
                                    <!-- First Name -->
                                    <div class="form-group">
                                        <label for="firstname">First Name</label>
                                        <input
                                            type="text"
                                            name="firstname"
                                            v-model="form.firstname"
                                            @keyup="updateProfileWidget"
                                            v-on:input="$v.form.firstname.$touch"
                                            :disabled="startedWithFirstNameProvided && (isEditingAccountHolderProfile || isAccountHolder)"
                                            :class="{'is-invalid': $v.form.firstname.$error, 'is-valid': $v.form.firstname.$dirty && !$v.form.firstname.$invalid}"
                                            class="form-control"
                                            novalidate
                                        >
                                        <!-- Validations -->
                                        <div v-show="!$v.form.firstname.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.firstname.$dirty && !$v.form.firstname.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.firstname.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                                <div class="col-lg-6">
                                    <!-- Last Name -->
                                    <div class="form-group">
                                        <label for="lastname">Last Name</label>
                                        <input
                                            type="text"
                                            name="lastname"
                                            v-model="form.lastname"
                                            @keyup="updateProfileWidget"
                                            v-on:input="$v.form.lastname.$touch"
                                            :disabled="startedWithLastNameProvided && (isEditingAccountHolderProfile || isAccountHolder)"
                                            :class="{'is-invalid': $v.form.lastname.$error, 'is-valid': $v.form.lastname.$dirty && !$v.form.lastname.$invalid}"
                                            class="form-control"
                                            novalidate
                                        >
                                        <!-- Validations -->
                                        <div v-show="!$v.form.lastname.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.lastname.$dirty && !$v.form.lastname.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.lastname.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-6">
                                    <!-- phonenumber -->
                                    <div class="form-group">
                                        <label for="phonenumber">Phone Number</label>
                                        <vue-tel-input
                                            v-model="form.phonenumber"
                                            :preferredCountries="['AU', 'US', 'GB', 'NZ']"
                                            :mode="'international'"
                                            />
                                        <!-- Validations -->
                                        <div v-show="!$v.form.phonenumber.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.phonenumber.$dirty && !$v.form.phonenumber.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.phonenumber.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                                <div class="col-lg-6">
                                    <!-- companyname Name -->
                                    <div class="form-group">
                                        <label for="companyname">Company Name</label>
                                        <input
                                            type="text"
                                            placeholder
                                            name="companyname"
                                            class="form-control"
                                            v-model="form.companyname"
                                            :disabled="startedWithCompanyNameProvided"
                                            v-on:input="$v.form.companyname.$touch"
                                            :class="{'is-invalid': $v.form.companyname.$error, 'is-valid': $v.form.companyname.$dirty && !$v.form.companyname.$invalid}"
                                        />
                                        <!-- Validations -->
                                        <div v-show="!$v.form.companyname.$dirty">
                                            &nbsp;
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.companyname.$dirty"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                    </div>
                                </div>
                            </div>

                            <sau-custom-fields
                                ref="personalCustom"
                                column-class="col-lg-6"
                                :fields="personalCustomFields"
                                v-if="personalCustomFields.length"
                            ></sau-custom-fields>
                        </div>
                    </div>
                </div>

                <div class="-m-4 pt-4 flex flex-col md:flex-row">
                    <div class="m-4 w-full md:w-1/4">
                        <h4>Address Details</h4>
                        <p class="text-sm">
                            Your billing address information is needed for invoicing.
                        </p>
                    </div>
                    <div class="m-4 w-full md:w-3/4">
                        <div class="border p-8">
                            <div class="row">
                                <div class="col">
                                    <!-- address 1 -->
                                    <div class="form-group">
                                        <label for="address1">Address 1</label>
                                        <input
                                            type="text"
                                            name="address1"
                                            v-model="form.address1"
                                            v-on:input="$v.form.address1.$touch"
                                            :class="{'is-invalid': $v.form.address1.$error, 'is-valid': $v.form.address1.$dirty && !$v.form.address1.$invalid}"
                                            class="form-control"
                                            placeholder
                                        >
                                        <!-- Validations -->
                                        <div v-show="!$v.form.address1.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.address1.$dirty && !$v.form.address1.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.address1.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col">
                                    <!-- Address 2 -->
                                    <div class="form-group">
                                        <label for="address1">Address 2</label>
                                        <input
                                            type="text"
                                            name="address1"
                                            v-model="form.address2"
                                            v-on:input="$v.form.address2.$touch"
                                            :class="{'is-valid': $v.form.address2.$dirty}"
                                            class="form-control"
                                            placeholder
                                        >
                                    </div>
                                    <div>
                                        <small class="text-muted">&nbsp;</small>
                                    </div>
                                    <!-- default message -->
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-9">
                                    <!-- City -->
                                    <div class="form-group">
                                        <label for="city">City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            v-model="form.city"
                                            v-on:input="$v.form.city.$touch"
                                            :class="{'is-invalid': $v.form.city.$error, 'is-valid': $v.form.city.$dirty && !$v.form.city.$invalid}"
                                            class="form-control"
                                            placeholder
                                        >
                                        <!-- Validations -->
                                        <div v-show="!$v.form.city.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.city.$dirty && !$v.form.city.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.city.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                                <div class="col-lg-3">
                                    <!-- Postcode -->
                                    <div class="form-group">
                                        <label for="postcode">Postcode</label>
                                        <input
                                            type="text"
                                            name="postcode"
                                            v-model="form.postcode"
                                            v-on:input="$v.form.postcode.$touch"
                                            :class="{'is-invalid': $v.form.postcode.$error, 'is-valid': $v.form.postcode.$dirty && !$v.form.postcode.$invalid}"
                                            class="form-control"
                                            placeholder
                                        >
                                        <!-- Validations -->
                                        <div v-show="!$v.form.postcode.$dirty">
                                            <small class="text-muted">* Required</small>
                                        </div>
                                        <!-- default message -->
                                        <div
                                            v-show="$v.form.postcode.$dirty && !$v.form.postcode.$invalid"
                                        >&nbsp;</div>
                                        <!-- valid message -->
                                        <div
                                            v-if="!$v.form.postcode.required"
                                            class="invalid-feedback"
                                        >* Required</div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-6">
                                    <div class="form-group">
                                        <label for="country">Country</label>
                                        <country-select
                                            id="country"
                                            class="form-control"
                                            v-model="form.country"
                                            :country="form.country"
                                            topCountry="AU"
                                            :class="{'is-invalid': $v.form.country.$error, 'is-valid': $v.form.country.$dirty && !$v.form.country.$invalid}"
                                        />
                                        <small v-show="!$v.form.country.$dirty" class="text-muted">&nbsp;</small>
                                        <small v-show="$v.form.country.$dirty && !$v.form.country.$invalid" class="text-muted">&nbsp;</small>
                                    </div>
                                </div>

                                <div class="col-lg-6">
                                    <div class="form-group">
                                        <label for="state">State/Region</label>
                                        <region-select
                                            id="state"
                                            class="form-control"
                                            v-model="form.state"
                                            :country="form.country"
                                            :region="form.state"
                                            :regionName="true"
                                            :class="{'is-invalid': $v.form.state.$error, 'is-valid': $v.form.state.$dirty && !$v.form.state.$invalid}"
                                            :placeholder="'Select State/Region'"
                                            :disabled="! form.country"
                                        />
                                        <small v-show="!$v.form.state.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                        <small v-show="$v.form.state.$dirty && !$v.form.state.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="hasCustomFields" class="-m-4 pt-4 flex flex-col md:flex-row">
                    <div class="m-4 w-full md:w-1/4">
                        <h4>Other Details</h4>
                        <p class="text-sm">
                            Additional information and options.
                        </p>
                    </div>

                    <div class="m-4 w-full md:w-3/4">
                        <div class="border p-8">
                            <!-- Account holder custom fields -->
                            <template>
                                <sau-custom-fields
                                    ref="custom"
                                    :fields="customFields"
                                    v-if="customFields.length"
                                ></sau-custom-fields>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="pt-4 flex justify-end items-center">
                    <button
                        type="button"
                        @click="cancelUpdateProfile()"
                        class="text-blue hover:underline"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        class="btn ml-4"
                        :disabled="disabled"
                        :class="readyToSubmit"
                        @click="updateProfile()"
                    >
                        Update Profile
                        <i
                            v-if="isUpdating.profile"
                            class="far fa-spinner-third fa-spin"
                        ></i>
                    </button>
                </div>
            </form>
        </template>
    </div>
</template>

<script>
import {
    required,
    minLength,
    maxLength,
    numeric,
    email,
    sameAs
} from 'vuelidate/lib/validators';
import UpdateEmail from './_UpdateEmail';
import SauCustomFields from './_CustomFields';
import vSelect from 'vue-select'; // I don't think this is used.

import { RegionSelect, CountrySelect } from 'vue-country-region-select';
import { VueTelInput } from 'vue-tel-input';

export default {
    components: {
        'sau-update-email': UpdateEmail,
        'sau-custom-fields': SauCustomFields,
        vSelect,
        CountrySelect,
        RegionSelect,
        VueTelInput
    },
    mounted() {
        this.user = this.isEditingAccountHolderProfile
            ? this.accountHolder
            : this.userDetails;

        this.isAccountHolder = this.userDetails.whmcs_contact_id === 0;

        if (this.isEditingAccountHolderProfile || !this.user.whmcs_contact_id) {
            this.getClientCustomFields()
                .then(response => {
                    this.customFields = response.data.filter(f => f.fieldname !== 'ABN');
                    this.personalCustomFields = response.data.filter(f => f.fieldname === 'ABN');
                })
                .finally(() => this.ready = true);
        } else {
            this.ready = true;
        }

        for (var property in this.user) {
            if (this.form[property] != undefined) {
                this.form[property] = this.user[property];
            }
        }

        this.updateProfileWidget();

        this.startedWithLastNameProvided = !! this.form.lastname;
        this.startedWithFirstNameProvided = !! this.form.firstname;
        this.startedWithCompanyNameProvided = !! this.form.companyname;
    },
    data() {
        return {
            user: {},
            ready: false,
            popup: 'For security reasons, only the account holder may update their email address',
            customFields: [],
            personalCustomFields: [],
            addressDidSelect: false,
            profileAdded: false,
            isAccountHolder: false,
            startedWithLastNameProvided: false,
            startedWithFirstNameProvided: false,
            startedWithCompanyNameProvided: false,
            form: {
                firstname: '',
                lastname: '',
                email: '',
                phonenumber: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                postcode: '',
                country: '',
                companyname: ''
            },
            error: {
                message: 'We cant find it yet'
            },
            isUpdating: {
                profile: false
            }
        };
    },
    validations() {
        return {
            form: {
                firstname: { required },
                lastname: { required },
                email: { required, email },
                phonenumber: { required },
                companyname: {},
                address1: { required },
                address2: {},
                state: { required },
                city: { required },
                postcode: { required },
                country: { required }
            }
        };
    },
    methods: {
        updateProfileWidget() {
            this.$store.commit('setProfileWidget', {
                firstname: this.form.firstname,
                lastname: this.form.lastname,
                avatar: this.user.avatar
            });
        },
        cancelUpdateProfile() {
            this.$store.commit('setDisplayProfileUpdateForm', false);
            this.$store.commit('setIsEditingAccountHolderProfile', false);
        },
        updateProfile() {
            var url = '/api/clients/' + this.user.whmcs_client_id;

            if (this.user.whmcs_contact_id) {
                /** If account is contact - flag subaccount for form validation (required field) */
                this.form.subaccount = true;
                url = '/api/contacts/' + this.user.whmcs_contact_id;
            }

            /** See if its updating the  */
            if (
                this.isEditingAccountHolderProfile ||
                !this.user.whmcs_contact_id
            ) {
                if (this.$refs.custom) {
                    this.form.customfields = this.$refs.custom.customfields;

                    // Merge in personal custom fields.
                    Object.keys(this.$refs.personalCustom.customfields)
                        .forEach(key => {
                            this.form.customfields[key] = this.$refs.personalCustom.customfields[key];
                        });
                }
            }

            this.isUpdating.profile = true;

            this.putRequest(url, this.form)
                .then(response => {
                    if (response.data.status === 'success') {
                        if (
                            !this.userFeatures.hasOwnProperty(
                                'confirmed-address'
                            )
                        ) {
                            this.hideInfo('confirmed-address');
                        }

                        /** Update details in Vuex */
                        if (this.isEditingAccountHolderProfile) {
                            this.$store.commit(
                                'updateAccountHolder',
                                this.form
                            );
                            this.$store.commit(
                                'setIsEditingAccountHolderProfile',
                                false
                            );
                            this.hideInfo('confirmed-address', this.user.id);
                        } else {
                            this.$store.commit('updateUserDetails', this.form);
                        }

                        this.$swal({
                            type: 'success',
                            title: 'Profile Updated',
                            showConfirmButton: false,
                            timer: 3000
                        });
                    }

                    window.location = '/account';
                })
                .catch(error => {
                    this.$swal({
                        type: 'error',
                        title: 'There was an error updating your profile',
                        showConfirmButton: false,
                        timer: 5000
                    });
                    this.isUpdating.profile = false;
                });
        }
    },
    computed: {
        readyToSubmit() {
            return !this.$v.$invalid ? 'btn-success' : 'btn-light';
        },
        hasCustomFields() {
            return Object.keys(this.customFields).length > 0;
        },
        disabled() {
            return this.$v.$invalid || this.isUpdating.profile ? true : false;
        }
    }
};
</script>
<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
